// Migraciones de esquema de offline-v2 (STOR-04).
//
// Una sola fuente lógica de la escalera de versiones (V1_STATEMENTS en schema.js + V2/V3 aquí) que
// aplica runUserVersionMigration por PRAGMA user_version — IGUAL en web (wa-sqlite/OPFS) y en nativo
// (@capacitor-community/sqlite). El backend nativo NO tiene gestor de versiones (a diferencia del viejo
// addUpgradeStatement de Capacitor), por eso la escalera se aplica a mano.
//
// Módulo PURO (sin imports de motor): el gate de test puede leer DB_NAME/DB_VERSION si lo necesita.

export const DB_NAME = 'binnacle_offline_v2'
export const DB_VERSION = 11

// ── v2 — índices compuestos para keyset (seek-method) pagination ──────────
//
// REGLA STOR-04 (locked [02-02]): el array v1 (schema.js V1_STATEMENTS) NUNCA se edita; un cambio de
// esquema = una NUEVA entrada { toVersion: N }. ASÍ que estos índices viven AQUÍ, no en schema.js.
//
// Por qué estos dos índices: el helper keyset de domain/lists.js ordena por (updated_at DESC,
// client_uuid DESC) con el client_uuid como TIEBREAK estable (desempata filas con el mismo updated_at
// → paginación sin huecos/duplicados). Un índice compuesto que cubra exactamente el ORDER BY + el
// filtro deja el seek en O(log n) por página (vs el O(n) de OFFSET).
//   - answers: filtra por occurrence_id, ordena por (updated_at, client_uuid) → (occurrence_id, updated_at, client_uuid).
//   - tasks:   filtra por `state` (la columna real en schema.js — NO `status`), ordena igual → (state, updated_at, client_uuid).
//
// Idempotentes (IF NOT EXISTS): re-correr la migración no falla ni duplica.
export const V2_STATEMENTS = [
  'CREATE INDEX IF NOT EXISTS idx_ans_occ_keyset ON answers(occurrence_id, updated_at, client_uuid)',
  'CREATE INDEX IF NOT EXISTS idx_task_keyset ON tasks(state, updated_at, client_uuid)'
]

// ── v3 — índice (client_uuid, status) del outbox ───────────────────────────────────────────────
//
// Quién lo necesita: el predicado de purga de fotos (photos/purge.js — dos subqueries correladas
// `WHERE o.client_uuid = pm.parent_uuid AND o.status ...` POR CADA fila de photo_meta), el lookup de
// edits pendientes del guard LWW (apply-server-rows: `WHERE client_uuid=? AND status='pending'`) y la
// poda de filas done (sync/prune.js). Sin él, todas esas rutas son scan completo del outbox — barato
// con decenas de filas, lineal-por-foto con semanas de capturas acumuladas. El índice existente
// idx_outbox_status (status, next_attempt_at) sirve al drain FIFO, no a estos lookups por dueño.
export const V3_STATEMENTS = [
  'CREATE INDEX IF NOT EXISTS idx_outbox_client ON outbox(client_uuid, status)'
]

// ── v5 — índice de la FK de c_catalog_elements ────────────────────────────────────────────────
//
// c_catalog_elements(catalog_id) era la ÚNICA FK de catálogo sin índice (las hermanas ya lo tienen:
// idx_fg_form, idx_fi_group, idx_loc_org, idx_usr_org, idx_hier_parent). Es además la tabla con más
// filas potenciales por catálogo y el pull lazy hace scopedDelete por catalog_id en cada refresh
// (WHERE catalog_id=?), que sin índice es full scan. Entrada NUEVA (regla STOR-04: V1 nunca se edita).
export const V5_STATEMENTS = [
  'CREATE INDEX IF NOT EXISTS idx_catelem_catalog ON c_catalog_elements(catalog_id)'
]

// ── v6 — sync_notifications.user_id (dueño de la nota) para devices COMPARTIDOS ─────────────────
//
// Mismo motivo que v4 (outbox.user_id): las lecturas de la UI de sync (dead-letter drawer,
// listPhotoIssues) deben filtrar por usuario para que B no vea/accione notas de A. La columna se
// añadió TAMBIÉN al CREATE TABLE v1 (instalación nueva la trae directo); para DBs ya creadas, el step
// v6 hace un ALTER condicional (chequea PRAGMA table_info, ALTER ADD COLUMN no es idempotente).

// ── v7 — índice de sync_notifications por usuario ────────────────────────────────────────────
//
// El step v6 agregó sync_notifications.user_id para que la UI (dead-letter drawer, listPhotoIssues)
// filtre por usuario, pero sin índice esas lecturas quedan como full scan. A diferencia de v6 (ALTER
// de columna, NO idempotente -> requiere el chequeo condicional de table_info), esto es un índice
// net-new puro: mismo patrón que v2/v3/v5 (CREATE INDEX IF NOT EXISTS, entrada NUEVA en migrations.js;
// el array v1 de schema.js NUNCA se toca, regla STOR-04 — idx_outbox_client e idx_catelem_catalog
// tampoco viven en schema.js, solo aquí). `dismissed` va como segunda columna porque el drawer filtra
// típicamente por (user_id, dismissed=0).
export const V7_STATEMENTS = [
  'CREATE INDEX IF NOT EXISTS idx_sync_notifications_user ON sync_notifications (user_id, dismissed)'
]

// ── v8 — outbox.done_at (epoch ms del flip a 'done') ─────────────────────────────────────────
//
// Por qué: la purga de binarios corría en el MISMO tick que el drain, así que una foto cuya op flipaba
// a done podía perder su binario en segundos. Eso dejaba una ventana en el REINGRESO (carrera
// reproducida en ui-photo-reentry-race.smoke.mjs): un find() en vuelo mientras el drain termina traía
// un snapshot del server SIN la foto, y el overlay (que la pinta desde el binario local) ya no tenía
// binario que resolver -> foto invisible hasta el siguiente reingreso. Con done_at, purge.js respeta
// una retención mínima tras el done (DONE_GRACE_MS): el binario sobrevive lo suficiente para que
// cualquier find() en vuelo lo superponga; después, el server ya la sirve.
// ALTER de columna (NO idempotente) -> chequeo condicional de table_info en runUserVersionMigration,
// mismo patrón que v4/v6; la columna también viaja en el CREATE de V1_STATEMENTS (excepción documentada
// a STOR-04 para columnas). done_at NULL = fila legacy o aún no-done (la purga la trata como purgable:
// COALESCE(done_at,0)).

// ── v9 — photo_meta.byte_length (integridad del binario de foto) ─────────────────────────────
//
// Por qué: NADA en el pipeline verificaba que el binario de una foto estuviera COMPLETO. El canal
// nativo escribe sin fsync (onwriteend = el kernel recibió los bytes, no que llegaron al flash) y un
// reinicio a nivel dispositivo podía dejar el archivo truncado MIENTRAS la fila SQLite (WAL)
// sobrevivía. Un JPEG truncado >0 bytes dispara `load` (no error) con las dimensiones completas y
// pinta las primeras filas + BLANCO — y el drain lo subía tal cual al server (reproducido en web:
// truncado al 30% -> slot blanco + multipart con el binario mutilado -> op done). byte_length guarda
// los bytes DECODIFICADOS esperados, sellados en capture(); resolveBlob/resolveBase64 comparan y un
// mismatch se trata como binario AUSENTE: no se pinta, no se sube, se marca 'missing' y la nota
// photo_missing pide la re-toma (toda esa maquinaria ya existía para el caso 0-bytes).
// NULL = fila legacy pre-v9 -> SIN verificación (jamás marcar missing una foto buena vieja).
// ALTER de columna (patrón v4/v6/v8): chequeo condicional en runUserVersionMigration + columna en el
// CREATE de V1_STATEMENTS (excepción documentada a STOR-04 para columnas).

// ── v10 - signatures: signed_by/signed_name/is_auditor/is_audited/audited_rol_name/audited_name ──
//
// Por qué: overlayPendingOfflineAddons/overlayPendingOfflineAnswers ya cierran el bug de re-entrada
// para esas dos familias (find() reconstruye this.current desde el SERVER y no trae lo aún no
// sincronizado -> se veía como "perdido" hasta que el outbox drenaba); a las firmas les faltaba
// EXACTAMENTE el mismo overlay (overlayPendingOfflineSignatures, ISS-994): nunca se agregó cuando
// offline-v2 (PR #145) shippeó. Pero a diferencia de addons/answers, `signatures` ni siquiera tenía
// las columnas necesarias para re-pintar una firma: OccurrenceSignaturesSection.vue lee signed_by/signed_name/
// is_auditor/is_audited/audited_rol_name/audited_name (además de signature_path, derivado del
// binario). Esos campos solo existían TRANSITORIAMENTE en el payload JSON del outbox
// (body.payload.signatures, el PUT completo), que no es atribuible con seguridad a una fila puntual
// (un solo save puede encolar varias firmas nuevas en el MISMO payload), de ahí la necesidad de
// columnas reales.
// Columnas NULLABLES: las filas legacy (pre-v10) siguen siendo válidas sin ellas; el overlay cae a
// derivar is_auditor/is_audited desde signature_kind SOLO cuando las columnas nuevas vienen null.
// ALTER de columna (patrón v4/v6/v8/v9, NO idempotente) -> chequeo condicional de table_info en
// runUserVersionMigration; las columnas también viajan en el CREATE de V1_STATEMENTS (excepción
// documentada a STOR-04 para columnas nullable: instalación nueva las trae directo, sin reintroducir
// el bug empty-DB #320: v1 sigue siendo UN solo upgrade sellando 1).

// ── v11 - occurrences.comments (comentarios adicionales de la ocurrencia, F-97) ──────────────────
//
// Por qué: los comentarios adicionales son de la OCURRENCIA, no de un ítem, así que no caben en
// `answers` (UNIQUE(occurrence_id, item_id)) ni en `addons` (también por ítem). Se guardaban solo con un
// PUT en línea y, sin red, se perdían (F-97). Ahora se capturan como las respuestas: fila local +
// op `occurrence.saveComments` en el outbox, en una transacción (OccurrencesRepository.saveComments).
// La fila local es la que pinta el overlay de reingreso mientras el op no drena (igual que v10 con las
// firmas): el texto no debe vivir solo en el body del outbox.
// Columna NULLABLE: las filas existentes siguen válidas sin ella. ALTER de columna (patrón v4/v6/v8/v9/v10,
// NO idempotente) -> chequeo condicional de table_info en runUserVersionMigration; la columna también va
// en el CREATE de V1_STATEMENTS (excepción documentada a STOR-04 para columnas nullable).

// ── v4 — outbox.user_id (dueño de cada op) para devices COMPARTIDOS ─────────────────────────────
//
// Por qué: el outbox es device-global y el drain usa el token del usuario ACTUAL. Sin owner, si el
// usuario A captura ocurrencias offline y no sincroniza, al entrar B en el mismo device se drenaban
// las ops de A bajo B. Con user_id por fila, el drain filtra `user_id IS NULL OR user_id = <actual>`,
// así B nunca pushea lo de A (y las filas legacy NULL drenan bajo cualquiera).
//
// EXCEPCIÓN puntual a STOR-04: la columna se añadió TAMBIÉN al CREATE TABLE de outbox en V1_STATEMENTS
// (instalación nueva + harness de tests la tienen directo). Añadir una columna NULLABLE al set v1
// consolidado NO reintroduce el bug empty-DB #320 (v1 sigue siendo UN solo upgrade sellando 1). Para
// las DBs ya creadas en v3 (sin la columna), el step v4 hace un ALTER condicional (ver
// runUserVersionMigration: chequea PRAGMA table_info antes, porque ALTER ADD COLUMN no es idempotente).

// Escalera de upgrades versionada, INCREMENTAL y ACUMULATIVA (la aplica runUserVersionMigration por
// PRAGMA user_version, sellando cada toVersion literal):
//   - v1 = UN solo upgrade consolidado (schema.js V1_STATEMENTS) → esquiva el bug empty-DB #320
//     (instalación nueva ejecuta TODO v1 y sella user_version=1).
//   - v2/v3 = índices net-new (entrada NUEVA cada uno; el array v1 NUNCA se edita, regla STOR-04).
// runUserVersionMigration importa V1_STATEMENTS (de schema) + V2/V3_STATEMENTS (de aquí) y los aplica
// igual en web y en nativo (@capacitor-community/sqlite NO tiene gestor de versiones nativo).
